/**
 * Rename a group or change its description (plan docs/plans/lms-plan.md 8.2,
 * 5.3, 18.20; WP-43). PATCH /admin/groups/:id sends only what changed; the
 * kind is fixed. A name another group of the network already holds is 409
 * LMS_CONFLICT and the dialog shows the server's message.
 */
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Loader2 } from 'lucide-react';
import Modal from '../../../../ui/Modal';
import { fieldCls, primaryBtnCls, secondaryBtnCls } from '../../../../ui/styles';
import { apiErrorMessage } from '../../../api/client';
import { GROUP_DESCRIPTION_MAX, GROUP_NAME_MAX, updateGroup } from '../../../api/groups';
import type { GroupChangeResult, GroupPatch, LmsGroup } from '../../../api/groups';
import { ErrorNote, labelCls } from '../../../components/pickers/shared';

export interface EditGroupModalProps {
  isOpen: boolean;
  group: LmsGroup;
  onClose: () => void;
  onSaved: (result: GroupChangeResult) => void;
}

export function EditGroupModal({ isOpen, group, onClose, onSaved }: EditGroupModalProps) {
  const [name, setName] = useState(group.name);
  const [description, setDescription] = useState(group.description ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setName(group.name);
    setDescription(group.description ?? '');
    setSaving(false);
    setError(null);
  }, [isOpen, group.name, group.description]);

  const trimmedName = name.trim();
  const trimmedDescription = description.trim();
  const patch: GroupPatch = {};
  if (trimmedName !== group.name) patch.name = trimmedName;
  if ((trimmedDescription || null) !== (group.description ?? null)) patch.description = trimmedDescription || null;
  const valid = trimmedName.length > 0 && trimmedName.length <= GROUP_NAME_MAX && trimmedDescription.length <= GROUP_DESCRIPTION_MAX;
  const changed = Object.keys(patch).length > 0;

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!valid || !changed || saving) return;
    setSaving(true);
    setError(null);
    try {
      onSaved(await updateGroup(group.id, patch));
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save the group.'));
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit group" maxWidth="max-w-lg">
      <form onSubmit={submit} className="space-y-4" data-testid="edit-group-form">
        <div>
          <label htmlFor="lms-edit-group-name" className={labelCls}>
            Name
          </label>
          <input
            id="lms-edit-group-name"
            data-testid="edit-group-name"
            className={`${fieldCls} min-h-[44px] w-full sm:min-h-0`}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          {trimmedName.length > GROUP_NAME_MAX && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">{`A name is at most ${GROUP_NAME_MAX} characters.`}</p>
          )}
        </div>
        <div>
          <label htmlFor="lms-edit-group-description" className={labelCls}>
            Description
          </label>
          <textarea
            id="lms-edit-group-description"
            data-testid="edit-group-description"
            className={`${fieldCls} w-full`}
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          {trimmedDescription.length > GROUP_DESCRIPTION_MAX && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">{`A description is at most ${GROUP_DESCRIPTION_MAX} characters.`}</p>
          )}
        </div>
        {error && <ErrorNote testId="edit-group-error">{error}</ErrorNote>}
        <div className="flex items-center justify-end gap-3 border-t border-gray-100 pt-4 dark:border-gray-700">
          <button type="button" className={secondaryBtnCls} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className={primaryBtnCls} disabled={!valid || !changed || saving} data-testid="edit-group-save">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Save
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default EditGroupModal;
